import React, { useEffect } from 'react';
import { Loader2, Settings as SettingsIcon } from 'lucide-react';
import { AziendaCard } from '../components/settings/AziendaCard';
import { LeadSettingsCard } from '../components/settings/LeadSettingsCard';
import { ModuliCard } from '../components/settings/ModuliCard';
import { PasswordCard } from '../components/settings/PasswordCard';
import { useAuth } from '../hooks/useAuth';
import { useSettingsStore } from '../store/useSettingsStore';

/**
 * Impostazioni dell'applicazione.
 *
 * Prima esisteva solo un pannellino nascosto sotto il nome dell'utente, con
 * due interruttori. Tutto il resto (dati azienda, giorni di ricontatto,
 * messaggio WhatsApp) viveva nel codice o nelle variabili d'ambiente: per
 * cambiare un numero di telefono bisognava ripubblicare l'app.
 *
 * La pagina ha due parti. "Il tuo account" (la password) la vede chiunque sia
 * collegato. Le impostazioni dell'azienda solo gli amministratori, e non solo
 * a video: la regola di accesso della tabella `app_settings` lascia scrivere
 * solo a loro, quindi chi le raggiungesse per altre strade non combinerebbe
 * comunque niente.
 */
export function SettingsPage() {
    const { user, profile } = useAuth();
    const { caricato, carica } = useSettingsStore();
    const isAdmin = profile?.role === 'admin';

    useEffect(() => {
        if (isAdmin && !caricato) void carica();
    }, [isAdmin, caricato, carica]);

    return (
        <div className="mx-auto w-full max-w-5xl space-y-4 p-0 md:space-y-6 md:p-6">
            <div className="flex items-start gap-3">
                <SettingsIcon className="mt-1 h-7 w-7 flex-shrink-0 text-gray-700" />
                <div>
                    <h1 className="break-words text-xl font-bold text-gray-900 md:text-3xl">Impostazioni</h1>
                    <p className="mt-1 text-sm text-gray-600">
                        {isAdmin
                            ? 'Il tuo account e le impostazioni dell’azienda.'
                            : 'Il tuo account.'}
                    </p>
                </div>
            </div>

            <section className="space-y-3">
                <TitoloSezione titolo="Il tuo account" />
                {user?.email ? (
                    <PasswordCard email={user.email} />
                ) : (
                    <Caricamento />
                )}
            </section>

            {isAdmin && (
                <section className="space-y-3 md:space-y-4">
                    <TitoloSezione
                        titolo="Azienda"
                        spiegazione="Valgono per tutti e non richiedono di ripubblicare l’app."
                    />
                    {caricato ? (
                        <>
                            <AziendaCard />
                            <LeadSettingsCard />
                            <ModuliCard />
                        </>
                    ) : (
                        <Caricamento />
                    )}
                </section>
            )}
        </div>
    );
}

function TitoloSezione({ titolo, spiegazione }: { titolo: string; spiegazione?: string }) {
    return (
        <div>
            <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">{titolo}</h2>
            {spiegazione && <p className="text-sm text-gray-500">{spiegazione}</p>}
        </div>
    );
}

function Caricamento() {
    return (
        <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </div>
    );
}

export default SettingsPage;
